'use client';
import type { CerFeatures } from '@slims/shared/data';
import { CerFeaturesView } from '@slims/ui/organisms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useParams } from '@/lib/next/navigation';
import { api, apiFetch } from '@/lib/utils/api';

export default function CerFeaturesPage() {
	const { id } = useParams<{ id: string }>();
	return <CerFeaturesWiring key={id} id={id as string} />;
}

function CerFeaturesWiring({ id }: { id: string }) {
	const queryClient = useQueryClient();
	const { refetch } = useScopeTree();
	const query = useQuery({
		queryKey: ['cer-features-editor', id],
		queryFn: () =>
			api.get<{ nome: string; features: CerFeatures }>(`/api/admin/orgs/${id}/features`),
		enabled: !!id,
	});
	const [draft, setDraft] = useState<CerFeatures | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const features = draft ?? query.data?.features ?? null;
	const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(query.data?.features);
	async function save() {
		if (!features || saving) return;
		setSaving(true);
		setError(null);
		try {
			const res = await apiFetch(`/api/admin/orgs/${id}/features`, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(features),
			});
			if (!res.ok) throw new Error('Salvataggio non riuscito. Riprova.');
			queryClient.setQueryData(['cer-features-editor', id], await res.json());
			setDraft(null);
			await refetch();
			await queryClient.invalidateQueries({ queryKey: ['cer-features'] });
			toast.success('Funzionalità aggiornate');
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Salvataggio non riuscito. Riprova.');
		} finally {
			setSaving(false);
		}
	}
	return (
		<CerFeaturesView
			name={query.data?.nome ?? ''}
			features={features}
			loading={query.isLoading}
			saving={saving}
			dirty={dirty}
			error={error ?? (query.isError ? 'Impossibile caricare le funzionalità. Riprova.' : null)}
			onChange={setDraft}
			onSave={() => void save()}
			onRetry={() => void query.refetch()}
		/>
	);
}
